import { faThumbsUp, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogProps, palettes, Textarea, Theme, Modal } from '@indigo-ag/component-module';
import { AgFinancingPhases, AgFinancingPhaseStatus } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { createStylesHook, ModalActionButtons } from 'shared';
import { Phase1Context } from '../BrazilGrowerFinance/phases/Phase1';
import { PanelInfo } from '../BrazilGrowerFinance/shared/PanelInfo';
import { PhaseRoutes } from '../Timeline/Timeline';
import styled from 'styled-components';

interface BackgroundCheckModalDecisionProps extends DialogProps {
  closeModal: () => void;
  status?: AgFinancingPhaseStatus;
  notesRequired?: boolean;
}

const useStyles = createStylesHook((theme: Theme) => ({
  alignRight: {
    marginLeft: 'auto !important'
  },

  custom: {
    alignItems: 'center'
  },

  containerDecisionButton: {
    display: 'flex',
    justifyContent: 'center'
  },

  decisionButton: {
    cursor: 'pointer',
    height: 100,
    width: 155,
    borderRadius: 8,
    backgroundColor: palettes.neutral['200'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'column',
    padding: '15px 8px',

    '& span': {
      textAlign: 'center'
    }
  },

  activeButton: {
    border: `1.5px solid ${palettes.brand.blue.base}`,
    color: palettes.brand.blue.base
  },

  size25Blue: {
    color: palettes.brand.blue.base,
    fontSize: theme.typography.pxToRem(25)
  },

  marginTop30: {
    marginTop: 30
  },

  marginRight30: {
    marginRight: 30
  }
}));

const ModalContent = styled.div`
  padding: 16px;
`;

const CustomModal = styled(Modal)`
  width: 100%;
  max-width: 520px;
`;

export const BackgroundCheckModalDecision: React.FunctionComponent<BackgroundCheckModalDecisionProps> = ({
  closeModal,
  open,
  status,
  notesRequired = false
}) => {
  /**
   * State
   */
  const [decision, setDecision] = useState<AgFinancingPhaseStatus>(status || AgFinancingPhaseStatus.NOT_STARTED);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * History
   */
  const history = useHistory();

  /**
   * Phase1 Context
   */
  const { updatePhase } = useContext(Phase1Context);

  useEffect(() => {
    setDecision(status || AgFinancingPhaseStatus.NOT_STARTED);
    setNotes('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClearDecision = () => setDecision(AgFinancingPhaseStatus.CLEAR);
  const onDeniedDecision = () => setDecision(AgFinancingPhaseStatus.DENIED);
  const onNotesChange = (e: React.ChangeEvent<any>) => {
    setNotes(e.target.value);
  };

  const updateDecision = () => {
    if (notesRequired && !notes) {
      setError(i18next.t('provideNote'));
      throw new Error('A note is required.');
    } else if (!!notes) {
      setError(undefined);
    }

    return updatePhase({ notes, status: decision }).then(() => {
      if (decision === AgFinancingPhaseStatus.CLEAR) {
        setShouldRedirect(true);
      }
    });
  };

  const onHandleRedirect = () => {
    if (shouldRedirect) {
      history.push(PhaseRoutes[AgFinancingPhases.PHASE_2]);
    }
  };

  const modalBody = (
    <ModalContent>
      <div className={classes.containerDecisionButton}>
        <div
          className={`${classes.decisionButton} ${classes.marginRight30} ${decision === AgFinancingPhaseStatus.CLEAR &&
            classes.activeButton}`}
          onClick={onClearDecision}
        >
          <FontAwesomeIcon className={classes.size25Blue} icon={faThumbsUp} />
          <span>{i18next.t('clearPhase')}</span>
        </div>
        <div
          className={`${classes.decisionButton} ${decision === AgFinancingPhaseStatus.DENIED && classes.activeButton}`}
          onClick={onDeniedDecision}
        >
          <FontAwesomeIcon className={classes.size25Blue} icon={faTimes} />
          <span>{i18next.t('denyPhase')}</span>
        </div>
      </div>
      <div className={classes.marginTop30}>
        <Textarea
          error={error}
          label={`${i18next.t('notes')} (${notesRequired ? i18next.t('required') : i18next.t('optional')})`}
          onChange={onNotesChange}
          value={notes}
        />
      </div>
      {decision === AgFinancingPhaseStatus.CLEAR ? (
        <div className={classes.marginTop30}>
          <PanelInfo backgroundColor={palettes.brand.blue.lightest} text={i18next.t('updateSFDCRecordMessage')} />
        </div>
      ) : null}
    </ModalContent>
  );

  return (
    <CustomModal
      open={open}
      title={i18next.t('decision')}
      onRequestClose={() => {
        closeModal();
        onHandleRedirect();
      }}
      renderContent={() => modalBody}
      renderActionArea={() => (
        <ModalActionButtons
          closeModal={closeModal}
          disabled={decision === AgFinancingPhaseStatus.NOT_STARTED}
          save={updateDecision}
          changeButtonName={i18next.t('submitDecision')}
        />
      )}
    />
  );
};
